import React, { Key, useEffect, useState } from 'react'
import { Button, Divider, Popconfirm, Tooltip, TreeProps, Form } from 'antd'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, EditOutlined, PlusCircleOutlined } from '@ant-design/icons'
import DirectoryTree from 'antd/es/tree/DirectoryTree'
import { generateStructureData, generateTreeDataNodes } from './utils'
import { useCreateCustomReportGroup } from '../../../../../features/customReport/queries/group/useCreateCustomReportGroup'
import { recursiveFind } from '../../../../../utils/helpers'
import { useCtxDeleteCustomReportGroup } from '../../../../../features/customReport/queries/group/useCtxDeleteCustomReportGroup'
import { useCtxUpdateCustomReportGroup } from '../../../../../features/customReport/queries/group/useCtxUpdateCustomReportGroup'
import { useUpdateStructure } from '../../../../../features/customReport/queries/structure/useUpdateStructure'
import { useCtxCustomReportStructure } from '../../../../../features/customReport/queries/structure/useCtxCustomReportStructure'
import { TranslationFields } from '../../../../../components/Form/TranslateFields'
import GroupForm, { GroupFormValues } from './GroupForm'
import { getTitleByLanguage } from '../customReportTable/utils'

export interface CustomReportData extends Partial<TranslationFields> {
  id: number
  groupId?: number
  order?: number
  key?: string
  name: string
  companyId?: string
  children?: CustomReportData[]
  isLeaf?: boolean
  group?: boolean
}

export interface CustomReportOrderData {
  id: number
  order: number
  type: 'CustomReport' | 'CustomReportGroup'
  groupId: number | null
}

const CustomReportOrder: React.FC = () => {
  const { t } = useTranslation()
  const { data: customReportTree } = useCtxCustomReportStructure()
  const { mutate: createCustomReportGroup } = useCreateCustomReportGroup()
  const { mutate: updateCustomReportGroup } = useCtxUpdateCustomReportGroup()
  const { mutate: deleteCustomReportGroup } = useCtxDeleteCustomReportGroup()
  const { mutateAsync: updateStucture } = useUpdateStructure()

  const [treeData, setTreeData] = useState<CustomReportData[]>([])
  const [addForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const buildNameFields = (values: GroupFormValues) => ({
    ...(values.nameEn !== undefined && { nameEn: values.nameEn }),
    ...(values.nameFi !== undefined && { nameFi: values.nameFi }),
    ...(values.nameSv !== undefined && { nameSv: values.nameSv })
  })

  const addGroup = async (path?: Key) => {
    try {
      const values = await addForm.validateFields()
      const item = recursiveFind(treeData, path as string, 'key')

      const groupData = {
        name: values.name,
        groupId: item?.id ?? null,
        ...buildNameFields(values)
      }

      createCustomReportGroup(groupData)
      addForm.resetFields()
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const updateGroup = async (id: number) => {
    try {
      const values = await editForm.validateFields()

      const groupData = {
        id,
        name: values.name,
        ...buildNameFields(values)
      }

      updateCustomReportGroup(groupData)
      editForm.resetFields()
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  useEffect(() => {
    setTreeData(generateTreeDataNodes(customReportTree as CustomReportData[]))
    return () => {
      setTreeData([])
    }
  }, [customReportTree])

  const onDrop: TreeProps<CustomReportData>['onDrop'] = async info => {
    const dropKey = info.node.key
    const dragKey = info.dragNode.key
    const dropPos = info.node.pos.split('-')
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])

    const loop = (
      data: CustomReportData[],
      key: React.Key,
      callback: (node: CustomReportData, i: number, data: CustomReportData[]) => void
    ) => {
      for (let i = 0; i < data.length; i += 1) {
        if (data[i].key === key) {
          return callback(data[i], i, data)
        }
        if (data[i].children) {
          loop(data[i].children!, key, callback)
        }
      }
      return true
    }
    const data = [...treeData]

    let dragObj: CustomReportData
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1)
      dragObj = item
    })

    if (!info.dropToGap) {
      loop(data, dropKey, item => {
        // eslint-disable-next-line no-param-reassign
        item.children = item.children || []
        item.children?.unshift(dragObj)
      })
    } else if (
      ((info.node as any).props.children || []).length > 0 &&
      (info.node as any).props.expanded &&
      dropPosition === 1
    ) {
      loop(data, dropKey, item => {
        // eslint-disable-next-line no-param-reassign
        item.children = item.children || []
        item.children?.unshift(dragObj)
      })
    } else {
      let ar: CustomReportData[] = []
      let i: number
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr
        i = index
      })
      if (dropPosition === -1) {
        ar.splice(i!, 0, dragObj!)
      } else {
        ar.splice(i! + 1, 0, dragObj!)
      }
    }
    const changedRow = info.dragNode

    const structuredData = generateStructureData(data, [changedRow.id])
    await updateStucture(structuredData)
    setTreeData(data)
  }

  const renderTranslationForm = (form: any, initialValues?: Partial<GroupFormValues>) => (
    <GroupForm form={form} initialValues={initialValues} />
  )

  const addNewGroup = (path?: Key) => {
    return (
      <Tooltip title={t('customReportPage:addReportGroup')}>
        <Popconfirm
          title={t('customReportPage:addReportGroup')}
          description={renderTranslationForm(addForm)}
          onConfirm={() => addGroup(path)}
          onCancel={() => addForm.resetFields()}
          okText={t('global:save')}
          cancelText={t('global:cancel')}
          placement="topRight"
        >
          {path ? (
            <PlusCircleOutlined style={{ fontSize: 17 }} />
          ) : (
            <Button style={{ marginLeft: 8 }} type="primary">
              {t('customReportPage:addReportGroupToRoot')}
            </Button>
          )}
        </Popconfirm>
      </Tooltip>
    )
  }

  const editGroup = (id: number, item: CustomReportData) => {
    const initialValues = {
      name: item.name,
      nameEn: item.nameEn,
      nameFi: item.nameFi,
      nameSv: item.nameSv
    }

    return (
      <>
        <Divider type="vertical" />
        <Tooltip title={t('customReportPage:editReportGroupName')}>
          <Popconfirm
            title={t('customReportPage:editReportGroupName')}
            description={renderTranslationForm(editForm, initialValues)}
            onConfirm={() => updateGroup(id)}
            onCancel={() => editForm.resetFields()}
            onOpenChange={open => {
              if (open) {
                editForm.resetFields()
                editForm.setFieldsValue(initialValues)
              } else {
                editForm.resetFields()
              }
            }}
            okText={t('global:edit')}
            cancelText={t('global:cancel')}
            placement="topRight"
          >
            <EditOutlined style={{ fontSize: 17, color: 'black' }} />
          </Popconfirm>
        </Tooltip>
      </>
    )
  }

  const deleteGroup = (id: number, children?: CustomReportData['children']) => {
    return (
      <>
        <Divider type="vertical" />
        <Tooltip title={t('customReportPage:deleteReportGroup')}>
          <Popconfirm
            title={t('global:delete-confirm')}
            onConfirm={() => {
              deleteCustomReportGroup(id)
            }}
            okText={t('global:yes')}
            cancelText={t('global:no')}
            disabled={children && children?.length > 0}
          >
            <DeleteOutlined
              style={{
                fontSize: 17,
                color: children && children?.length > 0 ? 'grey' : 'red'
              }}
            />
          </Popconfirm>
        </Tooltip>
      </>
    )
  }

  return (
    <>
      <DirectoryTree
        className="draggable-tree"
        draggable
        blockNode
        onDrop={onDrop}
        selectable={false}
        expandAction={false}
        showLine
        allowDrop={({ dropNode, dropPosition }) => {
          if (dropNode.isLeaf && dropPosition === 0) {
            return false
          }
          return true
        }}
        treeData={treeData}
        titleRender={cat => (
          <span style={{ color: !cat.isLeaf ? '#0225a1' : undefined, height: 90, fontSize: 15 }}>
            {`${getTitleByLanguage(cat)}  `}
            <span style={{ float: 'right', marginRight: 50 }}>
              {!cat.isLeaf && [addNewGroup(cat.key), editGroup(cat.id, cat), deleteGroup(cat.id, cat.children)]}
            </span>
          </span>
        )}
      />
      <div style={{ marginTop: 20, float: 'right', marginRight: 50 }}>{addNewGroup()}</div>
    </>
  )
}

export default CustomReportOrder
